import React from 'react'
import { Section, SectionTitle, SubSection, SubSectionTitle, Text, List, Table, CodeBlock } from '../styles'

export default function ArchitectureSection() {
  return (
    <Section id="architecture">
      <SectionTitle>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 3h18v2H3V3m0 4h18v2H3V7m0 4h18v2H3v-2m0 4h18v2H3v-2m0 4h18v2H3v-2z"/>
        </svg>
        2. System Architecture
      </SectionTitle>
      
      <SubSection id="core-components">
        <SubSectionTitle>2.1 Core Components</SubSectionTitle>
        <Text>
          The platform's architecture is built on four primary layers that work
          together to provide a seamless and efficient development environment:
        </Text>
        <CodeBlock>
{`// System Architecture Layers
1. Interface Layer
   - Web Application (React + Three.js)
   - API Gateway
   - WebSocket Server
   - Real-time Event System

2. Orchestration Layer
   - Agent Manager
   - Task Scheduler
   - Resource Allocator
   - Event Bus
   - State Management

3. Execution Layer
   - AI Model Server
   - Code Generation Engine
   - Asset Generation Pipeline
   - Social Media Integration
   - Testing Framework

4. Infrastructure Layer
   - Blockchain Network
   - Distributed Storage
   - Compute Cluster
   - Monitoring System
   - Security Services`}
        </CodeBlock>
      </SubSection>

      <SubSection id="tech-stack">
        <SubSectionTitle>2.2 Technical Stack</SubSectionTitle>
        <Text>
          Our platform leverages cutting-edge technologies across all layers:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Component</th>
              <th>Technology</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Frontend Framework</td>
              <td>React + Vite</td>
              <td>Fast, responsive user interface</td>
            </tr>
            <tr>
              <td>3D Visualization</td>
              <td>Three.js + WebGL</td>
              <td>Real-time agent network visualization</td>
            </tr>
            <tr>
              <td>State Management</td>
              <td>Context API + Custom Hooks</td>
              <td>Efficient application state handling</td>
            </tr>
            <tr>
              <td>Blockchain</td>
              <td>Solana + Web3.js</td>
              <td>Decentralized data and transactions</td>
            </tr>
            <tr>
              <td>AI Models</td>
              <td>TensorFlow + PyTorch</td>
              <td>Agent intelligence and decision making</td>
            </tr>
            <tr>
              <td>Database</td>
              <td>Distributed NoSQL + IPFS</td>
              <td>Scalable data storage and retrieval</td>
            </tr>
          </tbody>
        </Table>
      </SubSection>

      <SubSection id="data-flow">
        <SubSectionTitle>2.3 Data Flow Architecture</SubSectionTitle>
        <Text>
          The platform implements a sophisticated data flow architecture that
          ensures efficient communication between components:
        </Text>
        <CodeBlock>
{`// Data Flow Example
{
  "flowType": "project_update",
  "timestamp": "2024-01-20T10:30:00Z",
  "source": {
    "type": "agent",
    "id": "coder_1",
    "component": "code_generation"
  },
  "destination": {
    "type": "system",
    "component": "orchestrator"
  },
  "payload": {
    "projectId": "proj_123",
    "updateType": "milestone_completed",
    "details": {
      "milestone": "backend_api",
      "status": "success",
      "metrics": {
        "testCoverage": 95,
        "performance": 98,
        "codeQuality": 92
      }
    },
    "nextActions": [
      {
        "type": "notify_frontend_team",
        "priority": "high"
      },
      {
        "type": "trigger_integration_tests",
        "priority": "medium"
      }
    ]
  },
  "metadata": {
    "version": "1.0",
    "priority": "high",
    "retry_count": 0,
    "trace_id": "trace_abc123"
  }
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="deployment">
        <SubSectionTitle>2.4 Deployment Model</SubSectionTitle>
        <Text>
          Our platform utilizes a hybrid deployment model that combines
          cloud-native services with edge computing capabilities:
        </Text>
        <List>
          <li>
            Cloud Infrastructure:
            <ul>
              <li>Kubernetes clusters for container orchestration</li>
              <li>Auto-scaling groups for demand management</li>
              <li>Global CDN for asset delivery</li>
              <li>Distributed caching layer</li>
              <li>Multi-region failover</li>
            </ul>
          </li>
          <li>
            Edge Computing:
            <ul>
              <li>Local agent execution for reduced latency</li>
              <li>Edge caching for frequently accessed data</li>
              <li>P2P communication between nearby nodes</li>
              <li>Local model inference</li>
            </ul>
          </li>
          <li>
            Blockchain Nodes:
            <ul>
              <li>Validator nodes for transaction processing</li>
              <li>RPC nodes for client interactions</li>
              <li>Archive nodes for historical data</li>
              <li>Smart contract execution environment</li>
            </ul>
          </li>
          <li>
            Development Environment:
            <ul>
              <li>Local development clusters</li>
              <li>CI/CD pipelines</li>
              <li>Testing environments</li>
              <li>Monitoring and debugging tools</li>
            </ul>
          </li>
        </List>
      </SubSection>
    </Section>
  )
}
